import { type EmitFn, computed, getCurrentInstance } from 'vue'

import {
  BaseInputProps,
  DateInputProps,
  FormInputType,
  NumericInputProps,
  Primitive,
  SearchInputProps,
  TextualInputProps,
} from './types'

export function randomIdentifier(): string {
  return Math.random().toString().replace('0.', '')
}

/** Trailing-edge only; adds delay on first interaction (e.g. clearing via X). TODO: add leading-edge invocation. */
export function useDebounce() {
  let timeoutId: ReturnType<typeof setTimeout>

  return function (callback: () => void, debounce: number) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => {
      callback()
    }, debounce)
  }
}

export function useComputedInput(
  props: BaseInputProps | SearchInputProps | DateInputProps,
  emit: EmitFn<['update:modelValue']>,
) {
  const withDebounce = useDebounce()
  return computed({
    get: () => props.modelValue,
    set: (value: Primitive | File | undefined) => {
      if ('debounce' in props && props.debounce) {
        withDebounce(
          () => {
            emit('update:modelValue', value)
          },
          props.type === 'date' ? 600 : 300,
        )
      } else {
        emit('update:modelValue', value)
      }
    },
  })
}

export function useComputedAttrs(
  props: BaseInputProps | DateInputProps | NumericInputProps | TextualInputProps,
  type: FormInputType,
) {
  return computed(() => ({
    name: props.id,
    id: props.id,
    required: type === 'checkbox' ? false : props.required,
    disabled: props.disabled || props.readOnly,
    placeholder: props.placeholder,
    min: 'min' in props ? props.min : undefined,
    max: 'max' in props ? props.max : undefined,
    maxlength: 'maxlength' in props ? props.maxlength : undefined,
    ...getCurrentInstance()?.proxy?.$attrs,
  }))
}

export type EventsToValidate = 'change' | 'blur' | 'validate' | 'invalid'
export function useEventsToValidate(
  emit: EmitFn<['change', 'blur', 'validate', 'invalid']>,
): Record<EventsToValidate, (e?: Event) => void> {
  return {
    change: () => emit('change'),
    blur: () => emit('blur'),
    validate: () => emit('validate'),
    invalid: (e) => {
      e?.preventDefault()
      emit('invalid')
    },
  }
}
